import React from 'react';
import { useNavigate, Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, Copy, KeyRound } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from '@/components/ui/toast';
import {
  AccessCard, KeyDraft, LimitsCard, NameCard, draftToBody,
} from './developer-key-form';

/**
 * Issue an API key — a full page rather than a dialog.
 *
 * Two reasons it isn't a modal: choosing scopes is a security decision that
 * deserves room to read, and the result is a secret shown exactly once. A
 * dialog invites a stray click on the overlay, and losing the key that way
 * means reissuing it and updating the partner.
 *
 * The flow is two steps in one page: compose → the key. Step two replaces the
 * form rather than navigating, so the secret never travels through router
 * state or a query string.
 */
export function DeveloperNewKeyPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [draft, setDraft] = React.useState<KeyDraft>({ name: '', scopes: [], expiresAt: '', rateLimit: '' });
  const [issued, setIssued] = React.useState<{ key: string; name: string; keyLast4: string } | null>(null);
  const [copied, setCopied] = React.useState(false);

  const create = useMutation({
    mutationFn: async () => {
      const r = await api<any>('/api-keys', { method: 'POST', body: JSON.stringify(draftToBody(draft)) });
      if (!r.success) throw new Error(r.error?.message || 'Failed to create the key');
      return r.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['/api-keys'] });
      setIssued({ key: data.key, name: data.name, keyLast4: data.keyLast4 });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  function copy() {
    if (!issued) return;
    navigator.clipboard?.writeText(issued.key).then(
      () => { setCopied(true); toast('Copied', 'Key copied to clipboard', 'success'); },
      () => toast('Copy failed', 'Clipboard not available — select the key and copy manually', 'error'),
    );
  }

  const canSubmit = draft.name.trim().length > 0 && draft.scopes.length > 0 && !create.isPending;

  // ── Step 2: the key, shown once ────────────────────────────────────────
  if (issued) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">Key issued</h1>

        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <KeyRound className="h-4 w-4" /> {issued.name}
            </CardTitle>
            <CardDescription>
              Copy this now and store it in your partner&rsquo;s secret manager. It cannot be shown again —
              from here on Atlas only keeps <code className="font-mono">{issued.keyLast4}</code> and a hash.
              If it is lost, issue a new key and revoke this one.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-stretch gap-2">
              <code className="flex-1 font-mono text-xs bg-muted/40 border rounded px-3 py-2 break-all leading-relaxed">
                {issued.key}
              </code>
              <Button variant={copied ? 'outline' : 'default'} onClick={copy} className="shrink-0">
                {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy</>}
              </Button>
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
              <p>Send it over something the recipient controls — a password manager share, not email or chat.</p>
              <p>
                They can try it immediately at{' '}
                <a href="/api/v1/docs/integrations" target="_blank" rel="noreferrer" className="text-primary hover:underline">
                  the integration API docs
                </a>{' '}
                — Authorize, paste, Try it out.
              </p>
            </div>

            <div className="flex gap-2 pt-1">
              <Button onClick={() => navigate('/admin/developer')}>Done</Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIssued(null); setCopied(false);
                  setDraft({ name: '', scopes: [], expiresAt: '', rateLimit: '' });
                }}
              >
                Issue another
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Step 1: compose ────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div>
        <Link to="/admin/developer" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> Developer
        </Link>
        <h1 className="text-2xl font-semibold mt-1">New API key</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Grants a third-party system access to the Atlas API. The key is shown once, on the next screen.
        </p>
      </div>

      <NameCard draft={draft} onChange={setDraft} />

      {/* Access is the decision that needs the room; limits are a short form,
          so it takes two thirds of the width on a wide screen rather than both
          cards stretching to fill it. */}
      <div className="grid gap-4 xl:grid-cols-3">
        <AccessCard draft={draft} onChange={setDraft} />
        <LimitsCard draft={draft} onChange={setDraft} />
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={() => create.mutate()} disabled={!canSubmit}>
          {create.isPending ? 'Creating…' : 'Create key'}
        </Button>
        <Button variant="outline" onClick={() => navigate('/admin/developer')} disabled={create.isPending}>
          Cancel
        </Button>
        {!canSubmit && !create.isPending && (
          <span className="text-xs text-muted-foreground">
            {draft.name.trim().length === 0 ? 'Name the key to continue' : 'Select at least one scope'}
          </span>
        )}
      </div>
    </div>
  );
}

export default DeveloperNewKeyPage;
